import React from 'react';
import { HelpCircle, Mic, FileText, Globe, Wand2, Image, Send, Euro, User, BookOpen } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const Help = () => {
  const sections = [
    {
      icon: <Mic className="w-6 h-6" />,
      title: "1. Créer un article",
      color: "cyan",
      steps: [
        "Depuis le Dashboard, cliquez sur le site où vous voulez publier (ou 'Multi-sites' pour plusieurs)",
        "Tapez ou dictez votre sujet dans la zone de texte principale",
        "Utilisez la dictée vocale de votre téléphone (clavier → microphone)",
        "Ajoutez des URLs de référence ou des documents si besoin"
      ]
    },
    {
      icon: <Wand2 className="w-6 h-6" />,
      title: "2. Prompt personnalisé",
      color: "purple",
      steps: [
        "Cliquez sur 'Prompt personnalisé' pour ouvrir la section",
        "Collez vos instructions ChatGPT ou autres prompts",
        "L'IA suivra vos instructions au lieu du comportement par défaut",
        "Utile pour des styles ou formats d'articles spécifiques"
      ]
    },
    {
      icon: <Image className="w-6 h-6" />,
      title: "3. Génération",
      color: "pink",
      steps: [
        "Cliquez sur 'Générer' et patientez 30-60 secondes",
        "L'IA génère un article de 800+ mots optimisé SEO",
        "Une image style comics années 60 est créée automatiquement",
        "Le chiffre clé et la légende sont extraits pour WordPress"
      ]
    },
    {
      icon: <Send className="w-6 h-6" />,
      title: "4. Publication WordPress",
      color: "green",
      steps: [
        "Relisez l'article généré et modifiez si nécessaire",
        "Choisissez 'Publier directement' ou 'Enregistrer en brouillon'",
        "L'article est publié avec : image, auteur, SEO, catégories, tags",
        "Les champs personnalisés (chiffre/légende) sont remplis automatiquement"
      ]
    },
    {
      icon: <User className="w-6 h-6" />,
      title: "5. Configuration profil",
      color: "amber",
      steps: [
        "Allez dans 'Profil' pour configurer vos paramètres",
        "Renseignez vos identifiants WordPress pour chaque site",
        "L'auteur sera automatiquement celui de votre compte WordPress",
        "Ajoutez des documents de style pour que l'IA imite votre écriture"
      ]
    },
    {
      icon: <Euro className="w-6 h-6" />,
      title: "6. Suivi des coûts",
      color: "blue",
      steps: [
        "Le tableau de bord 'Coûts' affiche vos dépenses IA",
        "Coût moyen : ~0.05€ par article (texte + image)",
        "Répartition par type (texte/image) et par site",
        "Historique mensuel pour suivre votre budget"
      ]
    }
  ];

  const tips = [
    "💡 Utilisez la dictée vocale de votre téléphone pour gagner du temps",
    "💡 Les documents de style permettent à l'IA d'imiter votre ton",
    "💡 Le mot-clé focus améliore significativement le score SEO",
    "💡 Vérifiez que vos identifiants WordPress sont configurés dans le Profil",
    "💡 Les images comics sont générées sans texte pour un meilleur rendu"
  ];

  const colorClasses = {
    cyan: "text-sky-400 bg-sky-500/10 border-sky-500/30",
    purple: "text-purple-400 bg-purple-500/10 border-purple-500/30",
    pink: "text-pink-400 bg-pink-500/10 border-pink-500/30",
    green: "text-green-400 bg-green-500/10 border-green-500/30",
    amber: "text-amber-400 bg-amber-500/10 border-amber-500/30",
    blue: "text-blue-400 bg-blue-500/10 border-blue-500/30"
  };

  return (
    <div className="min-h-screen p-4 md:p-8" data-testid="help-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold font-orbitron tracking-widest uppercase text-sky-400 text-glow mb-2 flex items-center">
            <HelpCircle className="w-10 h-10 mr-4" />
            Mode d'emploi
          </h1>
          <p className="text-slate-400">Guide complet d'utilisation d'Auto Edito</p>
        </div>

        {/* Quick Tips */}
        <Card className="mb-8 bg-gradient-to-r from-sky-500/5 to-purple-500/5 border-sky-500/20">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-sky-400 mb-4">Astuces rapides</h3>
            <div className="space-y-2">
              {tips.map((tip, index) => (
                <p key={index} className="text-sm text-slate-300">{tip}</p>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Sections */}
        <div className="space-y-6">
          {sections.map((section, index) => (
            <Card key={index} className={`${colorClasses[section.color]} border`}>
              <CardHeader>
                <CardTitle className={`text-xl font-orbitron flex items-center ${colorClasses[section.color].split(' ')[0]}`}>
                  {section.icon}
                  <span className="ml-3">{section.title}</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ol className="space-y-3">
                  {section.steps.map((step, stepIndex) => (
                    <li key={stepIndex} className="flex items-start">
                      <span className={`flex-shrink-0 w-6 h-6 rounded-full ${colorClasses[section.color]} flex items-center justify-center text-xs font-bold mr-3`}>
                        {stepIndex + 1}
                      </span>
                      <span className="text-slate-300">{step}</span>
                    </li>
                  ))}
                </ol>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Version */}
        <div className="mt-8 text-center text-slate-600 text-sm">
          <p>Auto Edito v1.0 - Dernière mise à jour : Mars 2026</p>
          <p className="mt-1">Développé avec ❤️ par Emergent</p>
        </div>
      </div>
    </div>
  );
};

export default Help;
